import type {
  ActionSection,
  AnySection,
  CustomSection,
  DynamicMediaSection,
  PresetListSection,
  PresetSection,
  RelatedAppsSection,
  SectionItemBase,
  SiteMediaSection,
  ThemedSection,
  WelcomeSection,
  LogoSection,
} from '@wix/add-panel-common';
import { SectionBase, SectionType } from '@wix/add-panel-common';
import { anyPass, assocPath, filter, map, pipe } from 'ramda';
import { hasCustomSectionComponent } from '../../AddPanel/CategoryView/SectionView/sections/CustomSection/helpers';
import type { Utils } from '../../AddPanel/UtilsProvider';
import type { ConditionHelpers } from '../../AddPanel/UtilsProvider/UtilsProvider.types';
import { sortByIndex } from '../array';

type WithSectionType = Required<{ type: SectionType }>;

export const isThemedSection = (section: WithSectionType): section is ThemedSection =>
  section.type === SectionType.Theme || section.type === SectionType.Personalized;
export const isPresetSection = (section: WithSectionType): section is PresetSection =>
  section.type === SectionType.Preset;
export const isPresetListSection = (section: WithSectionType): section is PresetListSection =>
  section.type === SectionType.PresetList;
export const isMyWidgetsSection = (section: WithSectionType): boolean => section.type === 'SECTIONS_TYPES_MY_WIDGETS'; // TODO: change to SectionType.MyWidgets, not sure why getting it as undefined.
export const isDynamicMediaSection = (section: WithSectionType): section is DynamicMediaSection =>
  section.type === SectionType.DynamicMediaSectionsBox;
export const isSiteMediaSection = (section: WithSectionType): section is SiteMediaSection =>
  section.type === SectionType.SiteMediaSection;
export const isCustomSection = (section: WithSectionType): section is CustomSection =>
  section.type === SectionType.Custom;
export const isWelcomeSection = (section: WithSectionType): section is WelcomeSection =>
  section.type === SectionType.Welcome;
export const isLogoSection = (section: WithSectionType): section is LogoSection => section.type === SectionType.Logo;
export const isActionSection = (section: WithSectionType): section is ActionSection =>
  section.type === SectionType.Action;
export const isRelatedAppsSection = (section: WithSectionType): section is RelatedAppsSection =>
  section.type === SectionType.RelatedApps;
export const shouldRenderSectionOnSearchQuery = (section: WithSectionType): boolean => isDynamicMediaSection(section);

interface CanRenderSectionUtils {
  isSectionAvailable?: ConditionHelpers['isSectionAvailable'];
  hasSectionStyle: Utils['hasSectionStyle'];
}

export const canRenderSection =
  ({ hasSectionStyle, isSectionAvailable }: CanRenderSectionUtils) =>
  (section: AnySection) => {
    if (isSectionAvailable && !isSectionAvailable(section)) {
      return false;
    }

    if (isCustomSection(section)) {
      return hasCustomSectionComponent(section);
    }

    if (isThemedSection(section)) {
      try {
        return hasSectionStyle(section);
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    return true;
  };

const shouldAddItemsToSection = anyPass<AnySection>([isPresetSection, isPresetListSection, isActionSection]);

const addItems = (allItems: SectionItemBase[], allSections: AnySection[]) => {
  const itemsBySection = new Map<string, SectionItemBase[]>(allSections.map((section) => [section.systemId, []]));
  for (const item of allItems) {
    if (item.sectionIds) {
      for (const sectionId of item.sectionIds) {
        //it's guaranteed that sectionIds are unique, if not - we will have duplicates.
        itemsBySection.get(sectionId)?.push(item); //ignoring empty array - unknown section
      }
    }
  }
  return (section: AnySection): AnySection => {
    if (!shouldAddItemsToSection(section)) {
      return section;
    }
    const itemsForSection = itemsBySection.get(section.systemId) || [];
    return assocPath(['props', 'items'], itemsForSection, section);
  };
};

export const transformSections = (
  sections: AnySection[],
  items: SectionItemBase[],
  renderSectionUtils: CanRenderSectionUtils,
): AnySection[] =>
  pipe<SectionBase[], SectionBase[], SectionBase[], AnySection[]>(
    filter(canRenderSection(renderSectionUtils)),
    sortByIndex,
    map(addItems(items, sections)),
  )(sections);
